import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import type { Deal } from "../api/types";
import type { DocumentMeta, ScenarioResponse } from "../api/client";
import { fmtSF, fmtCurrency } from "../utils/format";
import { prettyFieldLabel } from "../utils/labels";

interface ParseHealthCardProps {
  documents: DocumentMeta[];
  deal: Deal;
  scenarios: ScenarioResponse[];
  dealId: string;
  onEnhance?: (docId: string) => void;
  onReparse?: () => void;
}

type DocType = "rent_roll" | "operating_statement" | "cam_rec";

const DOC_TYPE_LABELS: Record<DocType, string> = {
  rent_roll: "Rent Roll",
  operating_statement: "Op Statement",
  cam_rec: "CAM Rec",
};

function getDocByType(docs: DocumentMeta[], type: DocType): DocumentMeta | null {
  return docs.find((d) => d.file_type === type && d.parse_status === "complete") ?? null;
}

function getConfidence(doc: DocumentMeta | null): number | null {
  if (!doc?.parse_result) return null;
  const pr = doc.parse_result as Record<string, unknown>;
  return (pr.confidence_score as number) ?? null;
}

function confidenceColor(score: number): string {
  if (score >= 80) return "bg-forest-500";
  if (score >= 50) return "bg-honey-500";
  return "bg-red-500";
}

function confidenceBadgeColor(score: number): string {
  if (score >= 80) return "bg-forest-100 text-forest-700 border-forest-200";
  if (score >= 50) return "bg-honey-100 text-honey-700 border-honey-200";
  return "bg-red-100 text-red-700 border-red-200";
}

function getKeyMetric(doc: DocumentMeta | null, type: DocType, deal: Deal): { text: string; negative?: boolean } {
  if (!doc?.parse_result) return { text: "Not uploaded" };
  const pr = doc.parse_result as Record<string, unknown>;

  if (type === "rent_roll") {
    const tenants = (pr.tenants as unknown[]) ?? [];
    const prop = deal.property_data;
    const sf = prop?.total_leasable_area;
    return { text: `${tenants.length} tenants${sf ? `, ${fmtSF(sf)}` : ""}` };
  }
  if (type === "operating_statement") {
    const years = (pr.years as Array<Record<string, unknown>>) ?? [];
    if (years.length > 0) {
      const latest = years[years.length - 1];
      const revenue = (latest.total_revenue as number) ?? 0;
      const expenses = (latest.total_expenses as number) ?? 0;
      // When revenue is missing, NOI is meaningless — don't display a misleading
      // negative figure that suggests the property is losing money.
      if (revenue <= 0 && expenses > 0) {
        return {
          text: `${years.length} yr${years.length > 1 ? "s" : ""}, NOI: revenue not parsed`,
        };
      }
      const noi = revenue - expenses;
      return {
        text: `${years.length} yr${years.length > 1 ? "s" : ""}, NOI ${fmtCurrency(noi)}`,
        negative: noi < 0,
      };
    }
    return { text: "Parsed" };
  }
  if (type === "cam_rec") {
    const camTenants = (pr.tenants as unknown[]) ?? [];
    const pools = (pr.expense_pools as unknown[]) ?? [];
    return { text: `${pools.length} pool${pools.length !== 1 ? "s" : ""}, ${camTenants.length} tenants` };
  }
  return { text: "Parsed" };
}

function getTopIssue(doc: DocumentMeta | null): string | null {
  if (!doc?.parse_result) return null;
  const pr = doc.parse_result as Record<string, unknown>;
  const flags = (pr.flags as Array<Record<string, string>>) ?? [];
  if (flags.length === 0) return null;
  const errorFlag = flags.find((f) => f.severity === "error");
  if (errorFlag) return errorFlag.message || errorFlag.field || "Parse error";
  const warnFlag = flags.find((f) => f.severity === "warning");
  if (warnFlag) return warnFlag.message || warnFlag.field || "Warning";
  return null;
}

function buildWarnings(
  docs: DocumentMeta[],
  deal: Deal,
): Array<{ text: string; tab: string; severity: "error" | "warning" | "info" }> {
  const warnings: Array<{ text: string; tab: string; severity: "error" | "warning" | "info" }> = [];
  const prop = deal.property_data;

  // Check lease types from property_data or parse result
  let tenants: Array<Record<string, unknown>> | null = null;
  if (prop?.tenants) {
    tenants = prop.tenants as unknown as Array<Record<string, unknown>>;
  } else {
    // Fall back to parse result tenants when property_data not built
    const rrDoc = getDocByType(docs, "rent_roll");
    if (rrDoc?.parse_result) {
      tenants = ((rrDoc.parse_result as Record<string, unknown>).tenants as Array<Record<string, unknown>>) ?? null;
    }
  }
  if (tenants && tenants.length > 0) {
    const pendingCount = tenants.filter((t) => {
      const lt = ((t.lease_type as string) || "").toLowerCase();
      return !lt || lt === "pending";
    }).length;
    if (pendingCount > 0 && pendingCount === tenants.length) {
      warnings.push({
        text: "All lease types are pending — reimbursements cannot be calculated",
        tab: "rent-roll",
        severity: "warning",
      });
    } else if (pendingCount > 0) {
      warnings.push({
        text: `${pendingCount} tenant${pendingCount > 1 ? "s" : ""} with pending lease type`,
        tab: "rent-roll",
        severity: "warning",
      });
    }
  }

  // Check OS partial years. Demoted to info — expense-only OS exports
  // are common (many property managers produce them), and flagging each
  // year as a warning inflates the severity count for a clean parse.
  const osDoc = getDocByType(docs, "operating_statement");
  if (osDoc?.parse_result) {
    const years = ((osDoc.parse_result as Record<string, unknown>).years as Array<Record<string, unknown>>) ?? [];
    for (const y of years) {
      if ((y.total_revenue as number) === 0 && ((y.total_expenses as number) ?? 0) > 0) {
        warnings.push({
          text: `${y.year || "Unknown"} column has $0 revenue — expense-only column (common for budget/OpEx-only exports)`,
          tab: "operating-statement",
          severity: "info",
        });
      }
    }
  }

  // Collect validation flags across all docs (from _validation.flags)
  // Include both errors and warnings, but cap per document to keep card concise
  for (const doc of docs) {
    if (!doc.parse_result) continue;
    const pr = doc.parse_result as Record<string, unknown>;
    const validation = pr._validation as { flags?: Array<Record<string, string>> } | undefined;
    const flags = validation?.flags ?? [];
    let docWarningCount = 0;
    for (const f of flags) {
      const label = f.field ? prettyFieldLabel(f.field) : "Parse";
      const message = f.message || "Issue detected";
      const text = message.startsWith(label) ? message : `${label}: ${message}`;
      if (f.severity === "error") {
        // Errors are never capped — always show them
        warnings.push({
          text,
          tab: "documents",
          severity: "error" as const,
        });
      } else if (f.severity === "warning") {
        if (docWarningCount >= 2) continue;
        warnings.push({
          text,
          tab: "documents",
          severity: "warning" as const,
        });
        docWarningCount++;
      }
    }
    // Show (+N more) if warnings were hidden
    const hiddenWarnings = flags.filter(f => f.severity === "warning").length - docWarningCount;
    if (hiddenWarnings > 0) {
      warnings.push({
        text: `(+${hiddenWarnings} more warning${hiddenWarnings > 1 ? "s" : ""})`,
        tab: "documents",
        severity: "warning" as const,
      });
    }
  }

  // Sort: errors first, then warnings; total cap at 8
  warnings.sort((a, b) => (a.severity === "error" ? 0 : 1) - (b.severity === "error" ? 0 : 1));
  return warnings.slice(0, 8);
}

export default function ParseHealthCard({
  documents,
  deal,
  scenarios,
  dealId,
  onEnhance,
  onReparse,
}: ParseHealthCardProps) {
  const [expanded, setExpanded] = useState(true);

  const docTypes: DocType[] = ["rent_roll", "operating_statement", "cam_rec"];
  const docMap = Object.fromEntries(
    docTypes.map((t) => [t, getDocByType(documents, t)])
  ) as Record<DocType, DocumentMeta | null>;

  // Calculate weighted health score
  const scores: Array<{ score: number; weight: number }> = [];
  const rrConf = getConfidence(docMap.rent_roll);
  const osConf = getConfidence(docMap.operating_statement);
  const camConf = getConfidence(docMap.cam_rec);
  if (rrConf != null) scores.push({ score: rrConf, weight: 0.4 });
  if (osConf != null) scores.push({ score: osConf, weight: 0.4 });
  if (camConf != null) scores.push({ score: camConf, weight: 0.2 });

  const totalWeight = scores.reduce((s, x) => s + x.weight, 0);
  const healthScore =
    totalWeight > 0
      ? Math.round(scores.reduce((s, x) => s + x.score * x.weight, 0) / totalWeight)
      : null;

  const uploadedCount = docTypes.filter((t) => docMap[t] != null).length;
  const warnings = buildWarnings(documents, deal);
  const errorCount = warnings.filter((w) => w.severity === "error").length;
  const warnCount = warnings.filter((w) => w.severity === "warning").length;
  const issueCount = errorCount + warnCount;

  // Find lowest-confidence doc for "Enhance" button
  const lowestDoc = documents
    .filter((d) => d.parse_status === "complete" && d.parse_result)
    .sort((a, b) => (getConfidence(a) ?? 100) - (getConfidence(b) ?? 100))[0];

  const anyLowConfidence = [rrConf, osConf, camConf].some((c) => c != null && c < 75);
  const summaryLine = issueCount > 0
    ? `${issueCount} issue${issueCount > 1 ? "s" : ""} need${issueCount === 1 ? "s" : ""} attention`
    : anyLowConfidence
      ? "Review parse results before sharing"
      : "All documents look good";

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm print:hidden">
      {/* Header bar — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-grove-50/30 transition-colors rounded-xl"
      >
        <div className="flex items-center gap-4">
          {healthScore != null && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${confidenceBadgeColor(healthScore)}`}>
              {healthScore}/100
            </span>
          )}
          <div className="text-left">
            <span className="text-sm font-semibold text-bark-700">Data Quality</span>
            <span className="text-xs text-bark-400 ml-3">
              {uploadedCount} of 3 documents &middot; {summaryLine}
            </span>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-bark-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-bark-400" />
        )}
      </button>

      {expanded && (
        <div className="px-6 pb-5 space-y-5 border-t border-grove-200 pt-4">
          {/* Document Status Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {docTypes.map((type) => {
              const doc = docMap[type];
              const conf = getConfidence(doc);
              const metric = getKeyMetric(doc, type, deal);
              const issue = getTopIssue(doc);
              return (
                <div key={type} className="border border-grove-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    {doc ? (
                      <CheckCircle2 className="w-4 h-4 text-forest-500" />
                    ) : (
                      <FileText className="w-4 h-4 text-bark-300" />
                    )}
                    <span className="text-xs font-semibold text-bark-700">{DOC_TYPE_LABELS[type]}</span>
                    {conf != null && (
                      <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded border ${confidenceBadgeColor(conf)}`}>
                        {conf}
                      </span>
                    )}
                  </div>
                  {conf != null && (
                    <div className="h-1.5 bg-grove-200 rounded-full overflow-hidden mb-2">
                      <div
                        className={`h-full rounded-full ${confidenceColor(conf)}`}
                        style={{ width: `${conf}%` }}
                      />
                    </div>
                  )}
                  <p className={`text-[11px] truncate ${metric.negative ? "text-red-600 font-medium" : "text-bark-500"}`}>{metric.text}</p>
                  {issue && (
                    <p className="text-[10px] text-honey-600 mt-1 truncate" title={issue}>
                      {issue}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Critical Warnings */}
          {warnings.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold uppercase tracking-wide text-bark-400 mb-2">
                Issues ({warnings.length})
              </h4>
              <div className="space-y-1.5">
                {warnings.map((w, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs border ${
                      w.severity === "error"
                        ? "bg-red-50 border-red-200 text-red-700"
                        : w.severity === "info"
                          ? "bg-grove-50 border-grove-200 text-bark-600"
                          : "bg-honey-50 border-honey-200 text-honey-700"
                    }`}
                  >
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <span className="flex-1">{w.text}</span>
                    <Link
                      to={`/deals/${dealId}/${w.tab}`}
                      className="text-[10px] font-semibold text-honey-700 hover:text-honey-600 whitespace-nowrap"
                    >
                      Fix &rarr;
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            {onEnhance && lowestDoc && (getConfidence(lowestDoc) ?? 100) < 100 && (
              <button
                onClick={() => onEnhance(lowestDoc.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-forest-700 bg-forest-50 border border-forest-200 rounded-lg hover:bg-forest-100 transition-colors"
              >
                <Sparkles className="w-3 h-3" />
                Enhance with AI
              </button>
            )}
            {onReparse && (
              <button
                onClick={onReparse}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-bark-600 bg-grove-50 border border-grove-200 rounded-lg hover:bg-grove-100 transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Re-parse All
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
